import { Link, useNavigate } from "react-router-dom";
import { useCart } from "../hooks/useCart";
import { useAuth } from "../hooks/useAuth";
import { useStoreSettings } from "../hooks/useStoreSettings";

export default function Cart() {
    const {
        cartItems,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartTotal,
        cartCount,
    } = useCart();
    const { isLoggedIn } = useAuth();
    const { settings, getShippingCost, getTaxBreakdown, getDefaultShipping } =
        useStoreSettings();
    const navigate = useNavigate();

    const handleCheckout = () => {
        if (!isLoggedIn) {
            navigate("/login", { state: { from: { pathname: "/checkout" } } });
        } else {
            navigate("/checkout");
        }
    };

    if (cartItems.length === 0) {
        return (
            <div className="section">
                <div className="container">
                    <div className="text-center py-20 max-w-sm mx-auto">
                        <div className="w-24 h-24 bg-neutral-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-10 h-10 text-neutral-400"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1.5}
                                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13l-1.5 6h13M7 13L5.4 5M10 21a1 1 0 100-2 1 1 0 000 2zm7 0a1 1 0 100-2 1 1 0 000 2z"
                                />
                            </svg>
                        </div>
                        <h1 className="text-2xl font-bold text-neutral-900 mb-3">
                            Your cart is empty
                        </h1>
                        <p className="text-neutral-500 text-sm mb-8">
                            Looks like you haven't added anything yet.
                        </p>
                        <Link to="/shop" className="btn-primary px-8 py-3">
                            Start Shopping
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    // Compute totals using WC settings
    const defaultShipping = getDefaultShipping(cartTotal);
    const shippingCost = defaultShipping
        ? getShippingCost(cartTotal, defaultShipping.id, defaultShipping.zone)
        : 0;
    const { subtotalTax, shippingTax, totalTax } = getTaxBreakdown(
        cartTotal,
        shippingCost,
    );
    const orderTotal = cartTotal + shippingCost + totalTax;
    const currencySymbol = settings?.currency_symbol || "$";

    // Free shipping progress
    const freeShippingMethod = settings?.shipping_methods?.find(
        (m) => m.type === "free_shipping",
    );
    const freeShippingMin = freeShippingMethod?.min_amount || 0;
    const toFreeShipping =
        freeShippingMin > 0 ? Math.max(0, freeShippingMin - cartTotal) : 0;
    const freeShippingPct =
        freeShippingMin > 0
            ? Math.min(100, (cartTotal / freeShippingMin) * 100)
            : 100;

    return (
        <div className="section">
            <div className="container">
                {/* Header */}
                <div className="flex items-center justify-between mb-8">
                    <div>
                        <h1 className="section-title">Shopping Cart</h1>
                        <p className="section-subtitle mb-0">
                            {cartCount} {cartCount === 1 ? "item" : "items"}
                        </p>
                    </div>
                    <button
                        onClick={clearCart}
                        className="text-sm text-red-500 hover:text-red-700 font-medium cursor-pointer transition-colors"
                    >
                        Clear Cart
                    </button>
                </div>

                <div className="flex flex-col lg:flex-row gap-8">
                    {/* ── Cart Items ── */}
                    <div className="flex-1 flex flex-col gap-4">
                        {/* Free shipping progress bar */}
                        {freeShippingMin > 0 && (
                            <div className="card p-4">
                                {toFreeShipping > 0 ? (
                                    <p className="text-sm text-neutral-700 mb-2">
                                        Add{" "}
                                        <strong className="text-primary-600">
                                            {currencySymbol}
                                            {toFreeShipping.toFixed(2)}
                                        </strong>{" "}
                                        more to get{" "}
                                        <strong>free shipping!</strong>
                                    </p>
                                ) : (
                                    <p className="text-sm text-green-700 font-semibold mb-2">
                                        🎉 You've unlocked free shipping!
                                    </p>
                                )}
                                <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-primary-500 rounded-full transition-all duration-500"
                                        style={{ width: `${freeShippingPct}%` }}
                                    />
                                </div>
                            </div>
                        )}

                        {cartItems.map((item) => (
                            <CartItem
                                key={item.key}
                                item={item}
                                onRemove={removeFromCart}
                                onUpdateQty={updateQuantity}
                                currencySymbol={currencySymbol}
                            />
                        ))}

                        <Link
                            to="/shop"
                            className="flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium mt-2 w-fit"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                                strokeWidth={2}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                            Continue Shopping
                        </Link>
                    </div>

                    {/* ── Order Summary ── */}
                    <div className="lg:w-80 shrink-0">
                        <div className="card p-6 sticky top-24">
                            <h2 className="text-lg font-bold text-neutral-900 mb-6">
                                Order Summary
                            </h2>

                            <div className="flex flex-col gap-3 text-sm mb-6">
                                <div className="flex justify-between text-neutral-600">
                                    <span>Subtotal ({cartCount} items)</span>
                                    <span className="font-medium">
                                        {currencySymbol}
                                        {cartTotal.toFixed(2)}
                                    </span>
                                </div>

                                <div className="flex justify-between text-neutral-600">
                                    <span>
                                        Shipping
                                        {defaultShipping && (
                                            <span className="text-neutral-400 text-xs ml-1">
                                                ({defaultShipping.title})
                                            </span>
                                        )}
                                    </span>
                                    <span
                                        className={`font-medium ${shippingCost === 0 ? "text-green-600" : ""}`}
                                    >
                                        {shippingCost === 0
                                            ? "Free"
                                            : `${currencySymbol}${shippingCost.toFixed(2)}`}
                                    </span>
                                </div>

                                {settings?.tax_enabled && (
                                    <>
                                        {subtotalTax > 0 && (
                                            <div className="flex justify-between text-neutral-600">
                                                <span>
                                                    Tax on items (
                                                    {settings.tax_rate}%)
                                                </span>
                                                <span className="font-medium">
                                                    {currencySymbol}
                                                    {subtotalTax.toFixed(2)}
                                                </span>
                                            </div>
                                        )}
                                        {shippingTax > 0 && (
                                            <div className="flex justify-between text-neutral-600">
                                                <span>Tax on shipping</span>
                                                <span className="font-medium">
                                                    {currencySymbol}
                                                    {shippingTax.toFixed(2)}
                                                </span>
                                            </div>
                                        )}
                                    </>
                                )}

                                {toFreeShipping === 0 &&
                                    freeShippingMin > 0 &&
                                    shippingCost > 0 && (
                                        <div className="bg-green-50 text-green-700 text-xs rounded-lg px-3 py-2 font-medium">
                                            ✓ Free shipping applied!
                                        </div>
                                    )}

                                <div className="border-t border-neutral-100 pt-3 flex justify-between font-bold text-base text-neutral-900">
                                    <span>Estimated Total</span>
                                    <span className="text-primary-600">
                                        {currencySymbol}
                                        {orderTotal.toFixed(2)}
                                    </span>
                                </div>
                            </div>

                            <button
                                onClick={handleCheckout}
                                className="btn-primary w-full py-3 cursor-pointer"
                            >
                                {isLoggedIn
                                    ? "Proceed to Checkout"
                                    : "Sign In to Checkout"}
                            </button>

                            {!isLoggedIn && (
                                <p className="text-xs text-neutral-400 text-center mt-3">
                                    You'll need to sign in before checkout
                                </p>
                            )}

                            {/* Trust badges */}
                            <div className="mt-6 flex flex-col gap-2">
                                {[
                                    { icon: "🔒", text: "Secure checkout" },
                                    {
                                        icon: "🚚",
                                        text:
                                            freeShippingMin > 0
                                                ? `Free shipping over ${currencySymbol}${freeShippingMin}`
                                                : "Free shipping available",
                                    },
                                    { icon: "↩️", text: "Easy 30-day returns" },
                                ].map((badge) => (
                                    <div
                                        key={badge.text}
                                        className="flex items-center gap-2 text-xs text-neutral-400"
                                    >
                                        <span>{badge.icon}</span>
                                        <span>{badge.text}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Cart Item ─────────────────────────────────────────────────────
function CartItem({ item, onRemove, onUpdateQty, currencySymbol }) {
    const { product, variation, quantity, price, key } = item;

    // Build variation label from resolved attribute names
    const variantLabel = variation?.attributes
        ?.map((a) => `${a.name}: ${a.label}`)
        .join(" · ");

    return (
        <div className="card p-4 flex gap-4">
            {/* Image */}
            <Link to={`/shop/${product.slug}`} className="shrink-0">
                <div className="w-20 h-20 rounded-lg overflow-hidden bg-neutral-100">
                    {variation?.image || product.image ? (
                        <img
                            src={variation?.image || product.image}
                            alt={product.name}
                            className="w-full h-full object-cover"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center text-neutral-300">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="w-8 h-8"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={1}
                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                />
                            </svg>
                        </div>
                    )}
                </div>
            </Link>

            {/* Info */}
            <div className="flex-1 min-w-0">
                <div className="flex justify-between gap-2">
                    <div className="min-w-0">
                        <Link
                            to={`/shop/${product.slug}`}
                            className="text-sm font-semibold text-neutral-800 hover:text-primary-600 transition-colors line-clamp-2 leading-snug"
                        >
                            {product.name}
                        </Link>
                        {variantLabel && (
                            <p className="text-xs text-neutral-400 mt-0.5">
                                {variantLabel}
                            </p>
                        )}
                    </div>
                    <button
                        onClick={() => onRemove(key)}
                        className="text-neutral-300 hover:text-red-500 transition-colors cursor-pointer shrink-0 p-1"
                    >
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="w-4 h-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth={2}
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <div className="flex items-center justify-between mt-3 flex-wrap gap-2">
                    {/* Quantity */}
                    <div className="flex items-center border border-neutral-200 rounded-lg overflow-hidden">
                        <button
                            onClick={() => onUpdateQty(key, quantity - 1)}
                            className="w-7 h-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 cursor-pointer transition-colors"
                        >
                            −
                        </button>
                        <span className="w-7 text-center text-xs font-semibold text-neutral-800">
                            {quantity}
                        </span>
                        <button
                            onClick={() => onUpdateQty(key, quantity + 1)}
                            className="w-7 h-7 flex items-center justify-center text-neutral-500 hover:bg-neutral-100 cursor-pointer transition-colors"
                        >
                            +
                        </button>
                    </div>

                    {/* Price */}
                    <div className="text-right">
                        <p className="text-sm font-bold text-primary-600">
                            {currencySymbol}
                            {(parseFloat(price) * quantity).toFixed(2)}
                        </p>
                        {quantity > 1 && (
                            <p className="text-xs text-neutral-400">
                                {currencySymbol}
                                {parseFloat(price).toFixed(2)} each
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
